import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const UIContext = createContext(null);

export function UIProvider({ children }) {
  const [notification, setNotification] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const [loadingCount, setLoadingCount] = useState(0);
  const [themeMode, setThemeMode] = useState(localStorage.getItem("themeMode") || "dark");

  const showNotification = useCallback((message, severity = "info") => {
    setNotification({
      open: true,
      message,
      severity,
    });
  }, []);

  const closeNotification = useCallback(() => {
    setNotification((prev) => ({
      ...prev,
      open: false,
    }));
  }, []);

  const startLoading = useCallback(() => {
    setLoadingCount((prev) => prev + 1);
  }, []);

  const stopLoading = useCallback(() => {
    setLoadingCount((prev) => Math.max(0, prev - 1));
  }, []);

  const toggleThemeMode = useCallback(() => {
    setThemeMode((prev) => (prev === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    localStorage.setItem("themeMode", themeMode);
  }, [themeMode]);

  useEffect(() => {
    const onApiError = (event) => {
      const message = event?.detail?.message || "Errore di comunicazione con il server";
      showNotification(message, "error");
    };

    const onUnauthorized = () => {
      showNotification("Sessione scaduta. Effettua nuovamente il login.", "warning");
    };

    window.addEventListener("app:api-error", onApiError);
    window.addEventListener("app:unauthorized", onUnauthorized);

    return () => {
      window.removeEventListener("app:api-error", onApiError);
      window.removeEventListener("app:unauthorized", onUnauthorized);
    };
  }, [showNotification]);

  const value = useMemo(
    () => ({
      notification,
      showNotification,
      closeNotification,
      isLoading: loadingCount > 0,
      startLoading,
      stopLoading,
      themeMode,
      toggleThemeMode,
    }),
    [notification, showNotification, closeNotification, loadingCount, startLoading, stopLoading, themeMode, toggleThemeMode]
  );

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
}

export function useUI() {
  const context = useContext(UIContext);
  if (!context) {
    throw new Error("useUI deve essere usato dentro UIProvider");
  }

  return context;
}
